/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import striptags from "striptags";
import MarkdownIt from "markdown-it";

import { IModelSpec } from "../../shared/IModelSpec";
import { TModel_FPI_MSP_2020_05_Handler } from "./Model_FPI_MSP_2020_05_Handler";
import { ModelInput, IModelInputBaseConfig } from "../../shared/model/ModelInput";
import { formatNumericValue } from "../../shared/helpers";
import { ModelOutput } from "../../shared/model/ModelOutput";

export function Model_FPI_MSP_2020_05_Component_ExportXLSX(
	spec: IModelSpec,
	handler: TModel_FPI_MSP_2020_05_Handler
) {

	const md = new MarkdownIt();

	// Add base
	const res = {
		columns: [
			{ width: 40 },		// Label
			{ width: 10 },		// Value
			{ width: 70 },		// Description
			{ width: 20 },		// Interpretation prefix
			{ width: 150 },		// Interpretation description
		],
		rows: [
			[ "ID Modelu", "", spec.id, "", "" ],
			[ "Název modelu", "", spec.title, "", "" ],
			[ "Verze modelu", "", spec.version, "", "" ],
			[ "", "", "", "", "" ],
			[ "Jméno podniku", "", handler.inputs.jmenoPodniku.get(), "", "" ],
			[ "", "", "", "", "" ],
			[ "Vstupy", "", "", "", "" ]
		] as Array<Array<any>>
	};

	// Add inputs
	for (const k in handler.inputs) {

		if (k === "jmenoPodniku") {
			continue;
		}

		const inputState = (handler.inputs[k] as ModelInput<any, any, IModelInputBaseConfig>).getState();
		const inputVal = inputState.value;

		res.rows.push([
			inputState.config.label || k,
			inputVal,
			inputState.config.suffix || "",
			"",
			inputState.config.description || "",
		]);

	}

	// Add outputs
	res.rows.push([ "", "", "", "", "" ]);
	res.rows.push([ "Výstupy", "", "", "", "" ]);

	for (const k in handler.outputs) {

		const outputState = (handler.outputs[k] as ModelOutput<any>).getState();

		const outputVal = (
			typeof outputState.value === "number"
			? outputState.value * (outputState.config.multiplier || 1)
			: String(outputState.value)
		);

		let interpretationPrefix = "";
		let interpretationContent = "";

		if (outputState.config.resultLabels) {
			for (let i = 0; i < outputState.config.resultLabels.length; i++) {

				const resultLabel = outputState.config.resultLabels[i];

				if (resultLabel.condition(outputState, handler)) {
					interpretationPrefix = striptags(md.renderInline(resultLabel.prefix))
						.replace(/(&gt;)/g, ">")
						.replace(/(&lt;)/g, "<")
						.replace(/^(\n)+/, "")
						.replace(/(\n)+$/, "")
						.replace(/\n/g, "; ");
					interpretationContent = striptags(md.renderInline(resultLabel.content));
					break;
				}

			}
		}

		res.rows.push([
			(outputState.config.label || k) + ( outputState.config.suffix ? ` (${outputState.config.suffix})` : "" ),
			outputVal,
			striptags(md.render(outputState.config.description || "")),
			interpretationPrefix,
			interpretationContent
		]);

	}

	return res;

}

export function Model_FPI_MSP_2020_05_Component_ExportHTML(
	spec: IModelSpec,
	handler: TModel_FPI_MSP_2020_05_Handler
) {

	const md = new MarkdownIt();

	const customName = handler.inputs.jmenoPodniku.get() || "";
	const docTitle = spec.id + ( customName ? " / " + customName : "" );

	const html = `<!doctype html>
<html>
	<head>
		<title>${striptags(docTitle)}</title>
	</head>
	<body>
		<h1>${striptags(spec.titleLong)}</h1>
		<p><em>${striptags(spec.id)}</em></p>

		<h2><small>Jméno podniku</small><br />${striptags(handler.inputs.jmenoPodniku.get())}</h2>

		<h2>Vstupy</h2>
		<table>
			<thead>
				<tr>
					<td align="left">Název</td>
					<td align="left">Hodnota</td>
					<td align="left">Popis</td>
				</tr>
			</thead>
			<tbody>
			${Object.keys(handler.inputs).map((k) => {

				if (k === "jmenoPodniku") {
					return "";
				}

				const inputState = (handler.inputs[k] as ModelInput<any, any, IModelInputBaseConfig>).getState();
				const inputVal = formatNumericValue(inputState.value, null, null, ",");

				return `
					<tr>
						<td>${striptags(inputState.config.label || k)}</td>
						<td>${striptags(inputVal)}${ inputState.config.suffix ? " " + inputState.config.suffix : "" }</td>
						<td>${striptags(inputState.config.description) || ""}</td>
					</tr>
				`;

			}).join("\n")}
			</tbody>
		</table>

		<h2>Výstupy</h2>

		<table>
			<thead>
				<tr>
					<td align="left">Název</td>
					<td align="left">Hodnota</td>
					<td align="left">Popis</td>
					<td align="left">Interpretace</td>
					<td align="left">&nbsp;</td>
				</tr>
			</thead>
			<tbody>
			${Object.keys(handler.outputs).map((k) => {

				const outputState = (handler.outputs[k] as ModelOutput<any>).getState();

				const outputVal = (
					typeof outputState.value === "number"
					? formatNumericValue(outputState.value, outputState.config.decimals, outputState.config.multiplier, ",")
					: String(outputState.value)
				);

				let interpretationPrefix = "";
				let interpretationContent = "";

				if (outputState.config.resultLabels) {
					for (let i = 0; i < outputState.config.resultLabels.length; i++) {

						const resultLabel = outputState.config.resultLabels[i];

						if (resultLabel.condition(outputState, handler)) {
							interpretationPrefix = md.renderInline(resultLabel.prefix);
							interpretationContent = md.renderInline(resultLabel.content);
							break;
						}

					}
				}

				return `
					<tr>
						<td>${striptags(outputState.config.label || k)}</td>
						<td>${striptags(outputVal)}${ outputState.config.suffix || "" }</td>
						<td>${striptags(md.render(outputState.config.description)) || ""}</td>
						<td>${interpretationPrefix}</td>
						<td>${interpretationContent}</td>
					</tr>
				`;

			}).join("\n")}
			</tbody>
		</table>
	</body>
</html>`;

	return html;

}
